/**
 * Fees the collector adds to an invoice at collection time.
 *
 * Rendered in two places, against the same draft rows: the invoice pane, and the month
 * grid when a single billed month is selected. Both attach to exactly one invoice —
 * the API binds `line_items` to one `invoice_id` and will not take them alongside
 * `months`, so there is no third case.
 */

import { FEE_TYPE_OPTIONS } from '../../services/feeHeadApi';
import type { FeeHeadRecord, FeeType } from '../../services/feeHeadApi';
import type { TransportRouteRecord } from '../../services/transportRouteApi';
import {
    formatCurrency, routeFare, lineItemNet, lineItemGross, round2,
    splitPreview, DISTRIBUTION_OPTIONS,
} from './feeLines';
import type { DraftLineItem, LineItemType, FeeDistribution } from './feeLines';
import { formatMonth } from './months';

/** What a row will actually charge, spelled out — ₹300 becoming ₹100 × 3 is the whole
    point of the feature, so it shouldn't take arithmetic to see it. */
function distributionHint(it: DraftLineItem, months: string[]): string {
    const gross = lineItemGross(it);
    const n = months.length;
    if (gross <= 0 || n === 0) return '';

    if (it.distribution === 'per_month') {
        return `${formatCurrency(gross)} on each of ${n} months = ${formatCurrency(round2(gross * n))}`;
    }
    if (it.distribution === 'once') {
        return `charged on ${formatMonth(months[months.length - 1])} only`;
    }
    /* The server does the real division (integer paise, remainder to the oldest months);
       this mirrors it so the collector sees the same figures they're about to commit. */
    const shares = splitPreview(gross, n);
    return shares.every((s) => s === shares[0])
        ? `${formatCurrency(shares[0])} on each of ${n} months`
        : `${shares.map(formatCurrency).join(' / ')}`;
}

interface AdditionalFeesProps {
    lineItems: DraftLineItem[];
    lineItemErrors: Record<string, Record<string, string>>;
    lineItemsError: string;

    /** The months being collected, ascending. Empty on a single-invoice payment. */
    months: string[];

    feeHeads: FeeHeadRecord[];
    feeHeadsLoading: boolean;
    /** Standard amount per fee head, from the class fee structure. Prefills a ticked row. */
    standardAmounts: Record<string, number>;
    /** Fee heads already on the invoice — an added fee must not duplicate one. */
    takenFeeHeadIds: Set<string>;

    routes: TransportRouteRecord[];
    routesLoading: boolean;

    /** What the payment already owed before these fees ("Balance", "Jun 2026", …). */
    baseLabel: string;
    baseAmount: number;
    lineItemsNetTotal: number;

    feeHeadLine: (headId: string) => DraftLineItem | undefined;
    toggleFeeHead: (head: FeeHeadRecord, checked: boolean) => void;
    updateLineItem: (key: string, patch: Partial<DraftLineItem>) => void;
    updateLineDiscount: (key: string, raw: string) => void;
    removeLineItem: (key: string) => void;
    addCustomLineItem: (type: LineItemType) => void;
    selectRoute: (key: string, route: TransportRouteRecord) => void;
}

export default function AdditionalFees({
    lineItems, lineItemErrors, lineItemsError, months,
    feeHeads, feeHeadsLoading, standardAmounts, takenFeeHeadIds,
    routes, routesLoading,
    baseLabel, baseAmount, lineItemsNetTotal,
    feeHeadLine, toggleFeeHead, updateLineItem, updateLineDiscount,
    removeLineItem, addCustomLineItem, selectRoute,
}: AdditionalFeesProps) {
    /* Catalog heads live in the checkbox list; everything else gets its own card below it. */
    const customLines = lineItems.filter((it) => it.type === 'transport' || it.feeHeadMode === 'new');

    /* With one month the three distributions are the same charge, so the choice is noise —
       a value is still sent, the API requires one. It only earns screen space from two up. */
    const showDistribution = months.length > 1;

    const distributionPicker = (it: DraftLineItem) => (
        <select
            className="collection__fee-dist"
            value={it.distribution}
            aria-label="How this fee applies across the months"
            onChange={(e) => updateLineItem(it.key, { distribution: e.target.value as FeeDistribution })}
        >
            {DISTRIBUTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    );

    return (
        <div className="collection__pay-field">
            <div className="collection__fees-head">
                <span className="collection__pay-label">Additional Fees</span>
                {lineItems.length > 0 && <span className="collection__fees-count">{lineItems.length}</span>}
            </div>

            {feeHeadsLoading ? (
                <div className="collection__fee-list-loading">
                    <svg className="collection__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                    <span>Loading fee heads…</span>
                </div>
            ) : feeHeads.length === 0 ? (
                <p className="collection__fee-list-empty">No fee heads in the catalog yet — add a custom fee below.</p>
            ) : (
                <div className={`collection__fee-list ${showDistribution ? 'collection__fee-list--dist' : ''}`}>
                    <div className="collection__fee-list-head">
                        <span>Fee Head</span>
                        <span>Amount</span>
                        <span>Discount</span>
                        {showDistribution && <span>Applies</span>}
                    </div>
                    {feeHeads.map((h) => {
                        const line = feeHeadLine(h.id);
                        const onInvoice = takenFeeHeadIds.has(h.id);
                        const err = line ? (lineItemErrors[line.key] ?? {}) : {};
                        const standard = standardAmounts[h.id];
                        const rowError = err.feeHead || err.amount || err.discount || err.distribution;
                        const hint = line ? distributionHint(line, months) : '';
                        return (
                            <div
                                key={h.id}
                                className={`collection__fee-line ${line ? 'collection__fee-line--on' : ''} ${onInvoice ? 'collection__fee-line--taken' : ''}`}
                            >
                                <label className="collection__fee-line-pick">
                                    <input
                                        type="checkbox"
                                        checked={!!line}
                                        disabled={onInvoice}
                                        onChange={(e) => toggleFeeHead(h, e.target.checked)}
                                    />
                                    <span className="collection__fee-line-name">{h.name}</span>
                                    {onInvoice
                                        ? <span className="collection__fee-line-note">on invoice</span>
                                        : h.is_optional
                                            ? <span className="collection__fee-line-note">optional</span>
                                            : null}
                                </label>

                                <div className={`collection__adhoc-amount-wrap collection__fee-line-box ${err.amount ? 'collection__field--error' : ''}`}>
                                    <span className="collection__adhoc-currency">₹</span>
                                    <input
                                        className="collection__adhoc-amount"
                                        type="number" min={0.01} step="0.01" placeholder="Amount"
                                        value={line ? line.amount : (standard ?? '')}
                                        disabled={!line}
                                        onChange={(e) => line && updateLineItem(line.key, { amount: e.target.value === '' ? '' : Number(e.target.value) })}
                                    />
                                </div>

                                <div className={`collection__adhoc-amount-wrap collection__fee-line-box ${err.discount ? 'collection__field--error' : ''}`}>
                                    <span className="collection__adhoc-currency">₹</span>
                                    <input
                                        className="collection__adhoc-amount"
                                        type="number" min={0.01} step="0.01" placeholder="Discount"
                                        value={line ? line.discountAmount : ''}
                                        disabled={!line}
                                        onChange={(e) => line && updateLineDiscount(line.key, e.target.value)}
                                    />
                                </div>

                                {showDistribution && (
                                    line
                                        ? distributionPicker(line)
                                        : <span className="collection__fee-dist-placeholder">—</span>
                                )}

                                {hint && <p className="collection__fee-line-hint">{hint}</p>}
                                {rowError && <p className="collection__pay-error collection__fee-line-error">{rowError}</p>}
                            </div>
                        );
                    })}
                </div>
            )}

            {customLines.map((it) => {
                const err = lineItemErrors[it.key] ?? {};
                const amountField = (
                    <div className={`collection__adhoc-amount-wrap collection__fee-amount ${err.amount ? 'collection__field--error' : ''}`}>
                        <span className="collection__adhoc-currency">₹</span>
                        <input
                            className="collection__adhoc-amount"
                            type="number" min={0.01} step="0.01"
                            placeholder={it.type === 'transport' ? 'Fare' : 'Amount'}
                            value={it.amount}
                            onChange={(e) => updateLineItem(it.key, { amount: e.target.value === '' ? '' : Number(e.target.value) })}
                        />
                    </div>
                );
                const optionalToggle = (
                    <label className="collection__fee-optional">
                        <input type="checkbox" checked={it.isOptional} onChange={(e) => updateLineItem(it.key, { isOptional: e.target.checked })} />
                        Optional
                    </label>
                );
                return (
                    <div className="collection__fee-card" key={it.key}>
                        <div className="collection__fee-card-head">
                            <span className="collection__fee-card-kind">{it.type === 'transport' ? 'Transport' : 'New Fee Head'}</span>
                            <button type="button" className="collection__fee-remove" onClick={() => removeLineItem(it.key)} aria-label="Remove fee">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                            </button>
                        </div>

                        {it.type === 'fee_head' ? (
                            <>
                                <div className="collection__fee-newhead">
                                    <input
                                        className={`collection__adhoc-desc collection__fee-newhead-name ${err.feeHead ? 'collection__field--error' : ''}`}
                                        type="text"
                                        placeholder="Fee head name (e.g. Picnic)"
                                        maxLength={100}
                                        value={it.feeHeadName}
                                        autoFocus
                                        onChange={(e) => updateLineItem(it.key, { feeHeadName: e.target.value })}
                                    />
                                    <div className="collection__fee-newhead-row">
                                        <select
                                            className="collection__fee-newhead-type"
                                            value={it.feeHeadType}
                                            onChange={(e) => updateLineItem(it.key, { feeHeadType: e.target.value as FeeType })}
                                        >
                                            {FEE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                        {optionalToggle}
                                    </div>
                                    <p className="collection__fee-hint">Saved to the catalog and reusable next time.</p>
                                </div>
                                <div className="collection__fee-primary">
                                    {amountField}
                                </div>
                                {err.feeHead && <p className="collection__pay-error">{err.feeHead}</p>}
                                {err.amount && <p className="collection__pay-error">{err.amount}</p>}

                                {!it.discountEnabled ? (
                                    <button type="button" className="collection__fee-link collection__fee-disc-toggle" onClick={() => updateLineItem(it.key, { discountEnabled: true })}>
                                        ＋ Add discount
                                    </button>
                                ) : (
                                    <div className="collection__fee-discount">
                                        <div className="collection__fee-discount-head">
                                            <span>Discount</span>
                                            <button type="button" className="collection__fee-link" onClick={() => updateLineItem(it.key, { discountEnabled: false, discountAmount: '', discountName: '' })}>Remove</button>
                                        </div>
                                        <div className="collection__fee-row">
                                            <div className={`collection__adhoc-amount-wrap collection__fee-amount ${err.discount ? 'collection__field--error' : ''}`}>
                                                <span className="collection__adhoc-currency">₹</span>
                                                <input
                                                    className="collection__adhoc-amount"
                                                    type="number" min={0.01} step="0.01" placeholder="Off"
                                                    value={it.discountAmount}
                                                    onChange={(e) => updateLineItem(it.key, { discountAmount: e.target.value === '' ? '' : Number(e.target.value) })}
                                                />
                                            </div>
                                            <input
                                                className="collection__adhoc-desc collection__fee-disc-name"
                                                type="text" placeholder="Discount name (optional)" maxLength={100}
                                                value={it.discountName}
                                                onChange={(e) => updateLineItem(it.key, { discountName: e.target.value })}
                                            />
                                        </div>
                                        {err.discount && <p className="collection__pay-error">{err.discount}</p>}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {it.prefilled && (
                                    <p className="collection__fee-hint">
                                        The student rides this route and nothing here bills it — remove the row if the fare isn't being collected.
                                    </p>
                                )}
                                {routesLoading ? (
                                    <div className="collection__route-loading">
                                        <svg className="collection__spinner" width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                                        <span>Loading routes…</span>
                                    </div>
                                ) : routes.length === 0 ? (
                                    <p className="collection__route-empty">No transport routes configured — enter the fare below.</p>
                                ) : (
                                    <div className="collection__route-list" role="radiogroup" aria-label="Transport route">
                                        {routes.map((r) => {
                                            const fare = routeFare(r);
                                            return (
                                                <label
                                                    key={r.id}
                                                    className={`collection__route-opt ${it.routeId === r.id ? 'collection__route-opt--on' : ''}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`route-${it.key}`}
                                                        checked={it.routeId === r.id}
                                                        onChange={() => selectRoute(it.key, r)}
                                                    />
                                                    <span className="collection__route-name">{r.title}</span>
                                                    {r.vehicle && <span className="collection__route-veh">{r.vehicle.vehicle_number}</span>}
                                                    <span className="collection__route-fare">
                                                        {fare === undefined ? 'No fare set' : formatCurrency(fare)}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="collection__fee-primary">
                                    {amountField}
                                    <div className={`collection__adhoc-amount-wrap collection__fee-amount ${err.discount ? 'collection__field--error' : ''}`}>
                                        <span className="collection__adhoc-currency">₹</span>
                                        <input
                                            className="collection__adhoc-amount"
                                            type="number" min={0.01} step="0.01" placeholder="Discount"
                                            value={it.discountAmount}
                                            onChange={(e) => updateLineDiscount(it.key, e.target.value)}
                                        />
                                    </div>
                                </div>
                                {err.amount && <p className="collection__pay-error">{err.amount}</p>}
                                {err.discount && <p className="collection__pay-error">{err.discount}</p>}
                            </>
                        )}

                        {showDistribution && (
                            <div className="collection__fee-dist-row">
                                <span className="collection__fee-dist-label">Applies</span>
                                {distributionPicker(it)}
                                <span className="collection__fee-dist-hint">{distributionHint(it, months)}</span>
                            </div>
                        )}
                        {err.distribution && <p className="collection__pay-error">{err.distribution}</p>}

                        <div className="collection__fee-net">
                            Net added{' '}
                            <strong>
                                {formatCurrency(
                                    it.distribution === 'per_month' && months.length > 1
                                        ? round2(lineItemNet(it) * months.length)
                                        : lineItemNet(it)
                                )}
                            </strong>
                        </div>
                    </div>
                );
            })}

            <div className="collection__fee-add">
                <button type="button" className="collection__adhoc-toggle" onClick={() => addCustomLineItem('fee_head')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Add custom fee
                </button>
                <button type="button" className="collection__adhoc-toggle" onClick={() => addCustomLineItem('transport')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Add transport
                </button>
            </div>

            {lineItemsError && <p className="collection__pay-error collection__fees-error">{lineItemsError}</p>}

            {lineItemsNetTotal > 0 && (
                <p className="collection__adhoc-total">
                    {baseLabel} {formatCurrency(baseAmount)} + added fees {formatCurrency(lineItemsNetTotal)} = <strong>{formatCurrency(round2(baseAmount + lineItemsNetTotal))}</strong>
                </p>
            )}
        </div>
    );
}
